export const PAYMENT_ACQUIRINGS_SETTING_KEY = 'paymentAcquirings'
export const NO_PAYMENT_ACQUIRING_ID = 'none'
export const NO_PAYMENT_ACQUIRING_OPTION = {
  value: NO_PAYMENT_ACQUIRING_ID,
  label: 'Без эквайринга',
}
export const MULTIPLE_PAYMENT_ACQUIRINGS_TOOLTIP =
  'Вы можете добавить два эквайринга вместо одного. Пользователь сможет выбрать один из двух эквайрингов по их названию при продлении обучения или покупке курса в каталоге.'

export interface ConnectedPaymentAcquiring {
  id: string
  providerName: string
  providerLabel: string
  title: string
  fields: Record<string, string>
}

const LEGACY_PROVIDER_NAMES: Record<string, string> = {
  alfabank: 'alfaBank',
  alfa_bank: 'alfaBank',
  alfa: 'alfaBank',
  alfaBank: 'alfaBank',
  YooKassa: 'yooCassa',
  yookassa: 'yooCassa',
  yooCassa: 'yooCassa',
  paykeeper: 'paykeeper',
  tbank: 'tbank',
  t_bank: 'tbank',
  tinkoff: 'tbank',
  ozonbank: 'ozonBank',
  ozonBank: 'ozonBank',
  tochkabank: 'tochkaBank',
  tochkaBank: 'tochkaBank',
  robokassa: 'robokassa',
  cloudpayments: 'cloudpayments',
  cloudPayments: 'cloudpayments',
  mandarin: 'mandarin',
  vtb: 'vtb',
  greenleaf: 'greenleaf',
  greenleavespay: 'greenleaf',
}

export const getPaymentBrandDisplayText = (value?: string | null): string =>
  String(value ?? '').replace(/mandarin|мандарин/gi, 'Импульс')

export const normalizeLegacyPaymentProviderName = (
  value?: string | null,
): string | null => {
  const providerName = String(value ?? '').trim()
  if (!providerName) return null

  return (
    LEGACY_PROVIDER_NAMES[providerName] ??
    LEGACY_PROVIDER_NAMES[providerName.toLowerCase()] ??
    providerName
  )
}

export const createLegacyPaymentAcquiring = (
  paymentSystem?: string | null,
): ConnectedPaymentAcquiring | null => {
  const providerName = normalizeLegacyPaymentProviderName(paymentSystem)
  if (!providerName) return null

  const label = getPaymentAcquiringDisplayName(providerName, [])

  return {
    id: providerName,
    providerName,
    providerLabel: label,
    title: label,
    fields: {},
  }
}

export const parsePaymentAcquirings = (
  value?: string | ConnectedPaymentAcquiring[] | null,
): ConnectedPaymentAcquiring[] => {
  if (Array.isArray(value)) return normalizePaymentAcquirings(value)
  if (!value) return []

  try {
    const parsed = JSON.parse(value)
    return normalizePaymentAcquirings(parsed)
  } catch (e) {
    return []
  }
}

export const mergePaymentAcquirings = (
  ...values: Array<string | ConnectedPaymentAcquiring[] | null | undefined>
): ConnectedPaymentAcquiring[] => {
  const rows = new Map<string, ConnectedPaymentAcquiring>()

  values.flatMap(parsePaymentAcquirings).forEach((row) => {
    const current = rows.get(row.id)
    if (!current || (!current.title && row.title)) {
      rows.set(row.id, row)
    }
  })

  return Array.from(rows.values())
}

export const getPaymentAcquiringsWithLegacyFallback = ({
  paymentAcquirings,
  legacyPaymentSystem,
}: {
  paymentAcquirings?: Array<
    string | ConnectedPaymentAcquiring[] | null | undefined
  >
  legacyPaymentSystem?: string | null
}): ConnectedPaymentAcquiring[] => {
  const configured = mergePaymentAcquirings(...(paymentAcquirings ?? []))
  if (configured.length) return configured

  const legacyAcquiring = createLegacyPaymentAcquiring(legacyPaymentSystem)
  return legacyAcquiring ? [legacyAcquiring] : []
}

export const normalizePaymentAcquirings = (
  rows: unknown,
): ConnectedPaymentAcquiring[] => {
  if (!Array.isArray(rows)) return []

  const normalized = rows
    .map((row: any, index) => ({
      id: String(row?.id ?? `${row?.providerName ?? 'acquiring'}_${index}`),
      providerName: String(row?.providerName ?? row?.name ?? ''),
      providerLabel: getPaymentBrandDisplayText(
        row?.providerLabel ?? row?.label ?? row?.providerName,
      ),
      title: getPaymentBrandDisplayText(
        row?.title ?? row?.customName ?? row?.providerLabel,
      ),
      fields: normalizeFields(row?.fields),
    }))
    .filter((row) => row.id && row.providerName)

  return normalized.sort((first, second) => {
    if (first.providerName === second.providerName) return 0
    if (first.providerName === 'mandarin') return -1
    if (second.providerName === 'mandarin') return 1
    return 0
  })
}

export const normalizeFields = (fields: unknown): Record<string, string> => {
  if (!fields) return {}

  if (typeof fields === 'string') {
    try {
      return normalizeFields(JSON.parse(fields))
    } catch (e) {
      return {}
    }
  }

  if (typeof fields !== 'object' || Array.isArray(fields)) return {}

  return Object.fromEntries(
    Object.entries(fields as Record<string, unknown>).map(([key, value]) => [
      key,
      String(value ?? ''),
    ]),
  )
}

export const getPaymentAcquiringOptions = (
  rows: ConnectedPaymentAcquiring[],
) => [
  NO_PAYMENT_ACQUIRING_OPTION,
  ...normalizePaymentAcquirings(rows).map((row) => ({
    value: row.id,
    label: getPaymentBrandDisplayText(
      row.title || row.providerLabel || row.providerName,
    ),
  })),
]

export const getDefaultPaymentAcquiringId = (
  rows: ConnectedPaymentAcquiring[],
): string | null => rows[0]?.id ?? null

export const isNoPaymentAcquiringId = (value?: string | null) =>
  !value ||
  ['none', 'no_acquiring', 'without_acquiring', '__none__', '0'].includes(
    String(value),
  )

export const getOnlinePaymentAcquiringIds = (
  values: Array<string | null | undefined>,
) =>
  values.filter(
    (value, index, rows): value is string =>
      !isNoPaymentAcquiringId(value) && rows.indexOf(value) === index,
  )

export const getEffectivePaymentAcquiringIds = (
  values: Array<string | null | undefined>,
  configured: ConnectedPaymentAcquiring[],
) => {
  const selectedIds = getOnlinePaymentAcquiringIds(values)
  if (selectedIds.length > 0) return selectedIds

  const isExplicitlyDisabled = values.some(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '' &&
      isNoPaymentAcquiringId(value),
  )
  if (isExplicitlyDisabled) return []

  return configured[0]?.id ? [configured[0].id] : []
}

export const getPaymentAcquiringDisplayName = (
  id: string,
  rows: ConnectedPaymentAcquiring[],
) => {
  const acquiring = rows.find((row) => row.id === id)
  if (acquiring) {
    return getPaymentBrandDisplayText(
      acquiring.title || acquiring.providerLabel || acquiring.providerName,
    )
  }

  const providerName = id.split('_')[0]
  const knownProviderLabels: Record<string, string> = {
    yooCassa: 'ЮKassa',
    yookassa: 'ЮKassa',
    alfa: 'Альфа',
    alpha: 'Альфа',
    alfaBank: 'Альфа',
    alfabank: 'Альфа',
    cloudpayments: 'CloudPayments',
    cloudPayments: 'CloudPayments',
    ozonBank: 'Ozon Банк',
    paykeeper: 'PayKeeper',
    robokassa: 'Robokassa',
    mandarin: 'Импульс',
    vtb: 'ВТБ',
    greenleaf: 'Greenleaf',
    greenleavespay: 'Greenleaf',
    tbank: 'Т-Банк',
    tinkoff: 'Т-Банк',
    tochka: 'Точка',
    tochkaBank: 'Точка',
  }

  return getPaymentBrandDisplayText(
    knownProviderLabels[providerName] ?? 'Эквайринг',
  )
}
